import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Form,
  Input,
  Select,
  Button,
  message,
  Space,
  Card,
  InputNumber,
  Tooltip,
  Alert,
  Tag,
  Typography,
  Divider,
  Checkbox,
  Row,
  Col,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import TokenTemplateModal, { useTokenTemplates } from './TokenTemplateModal';

const { Option } = Select;
const { Text } = Typography;

interface DisplayFormatFormValues {
  prefix: string;
  separator: string;
  date_field: string;
  counter_padding: number;
  start_number: number;
  reset_period: 'CALENDAR_YEAR' | 'FINANCIAL_YEAR';
  fy_start_month?: number;
  reset_group?: string;
  selected_lookups: string[];
  selected_date_parts: string[];
  include_counter: boolean;
}

interface DisplayIdConfigProps {
  entityId: string;
  entityType: string;
  entitySchema?: string;
  displayFormat?: any;
  maxCounter?: any;
  metadata?: any[];
  onSave?: () => void;
}

const DisplayIdConfig: React.FC<DisplayIdConfigProps> = ({
  entityId,
  entityType,
  entitySchema,
  displayFormat,
  maxCounter,
  metadata,
  onSave,
}) => {
  const [form] = Form.useForm<DisplayFormatFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const { templates, loading: templatesLoading, refetch: refetchTemplates } = useTokenTemplates();

  // Check if entity has display_id in metadata
  const hasDisplayId = useMemo(() => {
    if (!metadata || !Array.isArray(metadata)) return false;
    return metadata.some(
      (field) => field.key === 'display_id' || field.key?.toLowerCase() === 'display_id'
    );
  }, [metadata]);

  // Get available entity fields for lookup tokens
  const entityFields = useMemo(() => {
    if (!metadata || !Array.isArray(metadata)) return [];
    return metadata
      .filter(f => f.key?.endsWith('_id') && f.key !== 'id' && f.key !== 'display_id')
      .map(f => f.key);
  }, [metadata]);

  // Filter lookup tokens applicable to this entity
  const applicableLookups = useMemo(() => {
    return templates.lookup.filter(t => entityFields.includes(t.entity_field));
  }, [templates.lookup, entityFields]);

  // Get date/timestamp fields for date_field dropdown (exclude updated_at)
  const dateFields = useMemo(() => {
    if (!metadata || !Array.isArray(metadata)) return [];
    return metadata
      .filter(f => {
        const type = f.type?.toLowerCase() || '';
        const key = f.key?.toLowerCase() || '';
        const isDateType = type.includes('date') || type.includes('timestamp');
        const isExcluded = key === 'updated_at';
        return isDateType && !isExcluded;
      })
      .map(f => ({ key: f.key, display_name: f.display_name || f.key }));
  }, [metadata]);

  // Generate format string from selections
  const generateFormatString = (values: Partial<DisplayFormatFormValues>) => {
    const sep = values.separator || '-';
    const parts: string[] = [];
    
    if (values.prefix?.trim()) parts.push(values.prefix.trim().toUpperCase());
    
    (values.selected_lookups || []).forEach(name => {
      parts.push(`{${name}}`);
    });
    
    (values.selected_date_parts || []).forEach(name => {
      parts.push(`{${name}}`);
    });
    
    if (values.include_counter !== false) {
      parts.push('{COUNTER}');
    }
    
    return parts.join(sep);
  };

  // Generate token_config from selections
  const generateTokenConfig = (values: DisplayFormatFormValues) => {
    const tokenConfig: any[] = [];
    
    (values.selected_lookups || []).forEach(name => {
      const template = templates.lookup.find(t => t.name === name);
      if (template) {
        tokenConfig.push({
          type: 'lookup',
          token: `{${template.name}}`,
          entity_field: template.entity_field,
          lookup_schema: template.lookup_schema,
          lookup_table: template.lookup_table,
          lookup_value_field: template.lookup_value_field,
        });
      }
    });
    
    (values.selected_date_parts || []).forEach(name => {
      const template = templates.date_part.find(t => t.name === name);
      if (template) {
        tokenConfig.push({
          type: 'date_part',
          token: `{${template.name}}`,
          date_format: template.date_format,
        });
      }
    });
    
    if (values.include_counter !== false) {
      tokenConfig.push({ type: 'counter', token: '{COUNTER}' });
    }
    
    return tokenConfig;
  };

  // Parse existing config to form values
  const parseExistingConfig = (config: any): Partial<DisplayFormatFormValues> => {
    if (!config?.format) return {};
    
    const format = config.format;
    const tokenConfig = config.token_config || [];
    const counterConfig = config.counter_config || {};
    
    const sep = ['-', '_', '/'].find(s => format.includes(s)) || '-';
    const parts = format.split(sep);
    const prefix = parts.find((p: string) => !p.startsWith('{')) || '';
    
    const selectedLookups = tokenConfig
      .filter((t: any) => t.type === 'lookup')
      .map((t: any) => t.token?.replace(/[{}]/g, ''))
      .filter(Boolean);
    
    const selectedDateParts = tokenConfig
      .filter((t: any) => t.type === 'date_part')
      .map((t: any) => t.token?.replace(/[{}]/g, ''))
      .filter(Boolean);
    
    const includeCounter = tokenConfig.some((t: any) => t.type === 'counter');
    
    return {
      prefix,
      separator: sep,
      date_field: config.date_field || '',
      counter_padding: config.counter_padding || 4,
      start_number: config.start_number || 1,
      selected_lookups: selectedLookups,
      selected_date_parts: selectedDateParts,
      include_counter: includeCounter,
      reset_period: counterConfig.reset_period || 'CALENDAR_YEAR',
      fy_start_month: counterConfig.fy_start_month || 4,
      reset_group: counterConfig.reset_group,
    };
  };

  useEffect(() => {
    if (!templatesLoading) {
      const parsed = parseExistingConfig(displayFormat);
      form.setFieldsValue({
        prefix: '',
        separator: '-',
        date_field: 'created_at',
        counter_padding: 4,
        start_number: 1,
        selected_lookups: [],
        selected_date_parts: [],
        include_counter: true,
        reset_period: 'CALENDAR_YEAR',
        fy_start_month: 4,
        ...parsed,
      });
    }
  }, [displayFormat, templatesLoading, form]);

  const onFinish = async (values: DisplayFormatFormValues) => {
    if (!entityId) {
      message.error('No entity selected.');
      return;
    }

    const formatString = generateFormatString(values);
    const tokenConfig = generateTokenConfig(values);

    const counterConfig: any = { reset_period: values.reset_period };
    if (values.reset_period === 'FINANCIAL_YEAR') {
      counterConfig.fy_start_month = values.fy_start_month || 4;
    }
    if (values.reset_group) {
      counterConfig.reset_group = values.reset_group;
    }

    const newDisplayFormat = {
      ...displayFormat,
      format: formatString,
      date_field: values.date_field || 'created_at',
      counter_padding: values.counter_padding,
      start_number: values.start_number || 1,
      token_config: tokenConfig,
      counter_config: counterConfig,
    };

    setIsSaving(true);
    try {
      const { error } = await supabase
        .schema('core')
        .from('entities')
        .update({ display_format: newDisplayFormat })
        .eq('id', entityId);

      if (error) throw error;
      message.success('Configuration saved!');
      onSave?.();
    } catch (error: any) {
      message.error(`Failed to save: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasDisplayId) {
    return (
      <Alert
        message="Display ID Not Available"
        description={<><strong>{entitySchema}.{entityType}</strong> does not have a display_id column.</>}
        type="info"
        showIcon
        style={{ maxWidth: 600 }}
      />
    );
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          prefix: '', separator: '-', counter_padding: 4, start_number: 1,
          include_counter: true, reset_period: 'CALENDAR_YEAR', fy_start_month: 4,
          selected_lookups: [], selected_date_parts: [],
        }}
      >
        {/* Compact Format Builder - Single Row */}
        <Card 
          title="ID Format" 
          size="small" 
          style={{ marginBottom: 12 }}
          extra={
            <Button size="small" icon={<SettingOutlined />} onClick={() => setTemplateModalVisible(true)}>
              Manage Tokens
            </Button>
          }
        >
          <Row gutter={8} align="middle">
            <Col>
              <Form.Item name="prefix" style={{ marginBottom: 0 }}>
                <Input placeholder="Prefix" style={{ width: 70, textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="separator" style={{ marginBottom: 0 }}>
                <Select style={{ width: 50 }}>
                  <Option value="-">-</Option>
                  <Option value="_">_</Option>
                  <Option value="/">/</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item name="selected_lookups" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Lookup tokens"
                  loading={templatesLoading}
                  maxTagCount={2}
                  style={{ minWidth: 180 }}
                >
                  {applicableLookups.map(t => (
                    <Option key={t.name} value={t.name}>
                      <Tag color="blue" style={{ marginRight: 4 }}>{t.name}</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>{t.entity_field}</Text>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="selected_date_parts" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Date"
                  loading={templatesLoading}
                  maxTagCount={1}
                  style={{ width: 120 }}
                >
                  {templates.date_part.map(t => (
                    <Option key={t.name} value={t.name}>
                      <Tag color="green">{t.name}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="include_counter" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox><Tag color="purple">COUNTER</Tag></Checkbox>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="counter_padding" style={{ marginBottom: 0 }}>
                <InputNumber min={1} max={8} style={{ width: 50 }} placeholder="Pad" />
              </Form.Item>
            </Col>
          </Row>

          {applicableLookups.length === 0 && !templatesLoading && (
            <Alert
              message="No lookup tokens match this entity's fields"
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              action={<Button size="small" onClick={() => setTemplateModalVisible(true)}>Add Tokens</Button>}
            />
          )}

          {/* Format Preview */}
          <Form.Item noStyle shouldUpdate>
            {() => {
              const values = form.getFieldsValue();
              const preview = generateFormatString(values);
              const example = preview.replace('{COUNTER}', '0001').replace(/\{[A-Z_]+\}/g, 'XXX');
              return preview ? (
                <div style={{ marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                  <Text code>{preview}</Text>
                  <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>→ {example}</Text>
                </div>
              ) : null;
            }}
          </Form.Item>
        </Card>

        {/* Counter Settings - Compact */}
        <Card title="Counter Settings" size="small" style={{ marginBottom: 12 }}>
          <Row gutter={16}>
            <Col>
              <Form.Item label="Date Field" name="date_field" style={{ marginBottom: 0 }}>
                <Select 
                  placeholder="Use current date" 
                  style={{ width: 160 }} 
                  allowClear
                >
                  {dateFields.map(f => (
                    <Option key={f.key} value={f.key}>
                      {f.display_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Reset" name="reset_period" style={{ marginBottom: 0 }}>
                <Select style={{ width: 130 }}>
                  <Option value="CALENDAR_YEAR">Calendar Year</Option>
                  <Option value="FINANCIAL_YEAR">Financial Year</Option>
                </Select>
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate={(p, c) => p.reset_period !== c.reset_period}>
              {() => form.getFieldValue('reset_period') === 'FINANCIAL_YEAR' && (
                <Col>
                  <Form.Item label="FY Start" name="fy_start_month" style={{ marginBottom: 0 }}>
                    <Select style={{ width: 80 }}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <Option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', {month: 'short'})}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
            </Form.Item>
            <Col>
              <Form.Item noStyle shouldUpdate={(p, c) => p.selected_lookups !== c.selected_lookups}>
                {() => {
                  const lookups = form.getFieldValue('selected_lookups') || [];
                  return (
                    <Form.Item label="Group By" name="reset_group" style={{ marginBottom: 0 }}>
                      <Select placeholder="None" allowClear style={{ width: 120 }}>
                        {lookups.map((n: string) => <Option key={n} value={`{${n}}`}>{n}</Option>)}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Start #" name="start_number" style={{ marginBottom: 0 }}>
                <InputNumber min={1} style={{ width: 70 }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Current Counters */}
        {maxCounter && Object.keys(maxCounter).length > 0 && (
          <Card title="Current Counters" size="small" style={{ marginBottom: 12 }} extra={<Tag>Read-only</Tag>}>
            <Space wrap>
              {Object.entries(maxCounter).map(([k, v]) => (
                <Tag key={k} color="geekblue"><code>{k}</code>: <strong>{String(v)}</strong></Tag>
              ))}
            </Space>
          </Card>
        )}

        {/* Actions */}
        <Space>
          <Button type="primary" htmlType="submit" loading={isSaving} icon={<SaveOutlined />}>
            Save
          </Button>
          <Button onClick={() => form.setFieldsValue(parseExistingConfig(displayFormat))} icon={<ReloadOutlined />}>
            Reset
          </Button>
        </Space>
      </Form>

      <TokenTemplateModal
        visible={templateModalVisible}
        onClose={() => setTemplateModalVisible(false)}
        onTemplatesUpdated={refetchTemplates}
      />
    </div>
  );
};

export default DisplayIdConfig;
